import React, { useEffect, useRef, useState, useContext } from "react";
import { AppContext } from "../../App";
import { getProductMetadata } from "../../network/ApiService";
import "./ManageAccessCodeStepOne.scss";
import { getIsbnCode } from "../../network/ApiService";

const ManageAccessCodeStepOne = ({ onError, setIsLoading }) => {
    const [inputIdValue, setInputIdValue] = useState("");
    const [ppidNumber, setPpidNumber] = useState("");
    const [error, setError] = useState("");
    const [isLoadingISBN, setIsLoadingISBN] = useState(false);
    const [isValidISBN, setIsValidISBN] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const ppidTitleRef = useRef(null);
    const {
        setManageAccessCodeDetails,
        manageAccessCodeDetails,
        ppidTitle,
        setPpidTitle,
        getAccessCodeDetails,
        isLoadingAccessCodes,
        manageCodesError,
        setManageCodesError,
        setCurrentIsbn
    } = useContext(AppContext);

    useEffect(() => {
        setPpidTitle("");
    }, []);

    useEffect(() => {
        const checkOverflow = () => {
            if (ppidTitleRef.current) {
                setIsTruncated(
                    ppidTitleRef.current.scrollWidth > ppidTitleRef.current.clientWidth
                );
            }
        };

        checkOverflow();
        window.addEventListener("resize", checkOverflow);

        return () => {
            window.removeEventListener("resize", checkOverflow);
        };
    }, [ppidTitle]);

      useEffect(()=>{
        if(inputIdValue && !isLoadingAccessCodes && manageAccessCodeDetails){
          setManageCodesError("");
        }
        else if(inputIdValue && !isLoadingAccessCodes && !manageAccessCodeDetails && isValidISBN){
          setManageCodesError("Error fetching access code details");
        }
      },[isLoadingAccessCodes, manageAccessCodeDetails, isValidISBN]);

    const fetchAccessCodeDetails = async (isbn) => {
        try {
            const accessCodesFetchStatus = await getAccessCodeDetails(isbn, 1, 30);
        } catch (error) {
            console.error("Error fetching access codes:", error);
        }
    };

    const handleSearch = () => {
        setManageCodesError("");
        setPpidTitle("");
        setPpidNumber("");
        setIsValidISBN(false);
        // Clear current ISBN first to trigger reset in StepTwo
        setCurrentIsbn("");
        setManageAccessCodeDetails(null);

        if (inputIdValue.length !== 13) {
            setManageCodesError("ISBN or PPID not recognised please retype");
            return;
        }

        setIsLoadingISBN(true);
        setIsLoading(true);

        const subscription = getProductMetadata(inputIdValue).subscribe(
            (response) => {
                if (response && response.length > 0) {
                    setPpidTitle(response[0]?.resourceTitle || "");
                    setPpidNumber(response[0]?.productId || "");
                    setIsValidISBN(true);
                    setCurrentIsbn(response[0]?.isbn || inputIdValue);
                    fetchAccessCodeDetails(response[0]?.isbn || inputIdValue);
                    setManageCodesError("");
                } else {
                    setManageCodesError("This ISBN or PPID is not recognised.");
                    setPpidTitle("");
                    setPpidNumber("");
                    setIsValidISBN(false);
                    setCurrentIsbn("");
                }
                setIsLoadingISBN(false);
                setIsLoading(false);
            },
            (error) => {
                setManageCodesError("This ISBN or PPID is not recognised.");
                setPpidTitle("");
                setPpidNumber("");
                setIsValidISBN(false);
                setIsLoadingISBN(false);
                setIsLoading(false);
                if (onError) onError(error);
            }
        );
        return () => subscription.unsubscribe();
    };

    return (
        <div className="step-one-container">
            <div className="step-one-header">
                <span className="header-icon">1</span>
                <div className="header-text">
                    Search for the ISBN13 or PPID you want to find code orders for
                </div>
            </div>

            <div className="search-row">
                {/* ISBN Search Field */}
                <div className="search-container">
                    <div className="field-box">
                        <label className="field-label">
                            Enter the full ISBN13 or PPID and click search
                        </label>
                        {manageCodesError && (
                            <div className="error-message">
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/error-new.svg`}
                                    alt="Error"
                                    className="error-icon"
                                />
                                <span>{manageCodesError}</span>
                            </div>
                        )}
                        {isValidISBN && (
                            <div className="isbn-valid">
                                <img
                                    className="isbn-valid-icon"
                                    src={`${process.env.PUBLIC_URL}/images/success-new.svg`}
                                    alt="Valid"
                                />
                                <span>This is a valid ISBN or PPID</span>
                            </div>
                        )}
                        <div
                            className={`field-container isbn-field  isbn-input ${manageCodesError ? "error" : ""} ${!manageCodesError && isValidISBN ? "valid-isbn-input" : ""
                                }`}
                        >
                            <input
                                type="text"
                                className="field-input"
                                value={inputIdValue}
                                maxLength={13}
                                placeholder="Enter ISBN13 or PPID"
                                onPaste={(e) => {
                                    e.preventDefault();
                                    const pasteData = e.clipboardData
                                        .getData("text")
                                        .slice(0, 13);
                                    setInputIdValue(pasteData);
                                    setManageCodesError("");
                                    setPpidTitle("");
                                    setPpidNumber("");
                                }}
                                onChange={(e) => {
                                    const cleanedValue = e.target.value.slice(0, 13);
                                    setInputIdValue(cleanedValue);

                                    if (cleanedValue === "") {
                                        setManageCodesError("");
                                        setPpidTitle("");
                                        setPpidNumber("");
                                    }
                                }}
                            />

                            {inputIdValue && (
                                <button
                                    type="button"
                                    className="clear-icon"
                                    onClick={() => {
                                        setInputIdValue("");
                                        setManageCodesError("");
                                        setPpidTitle("");
                                        setPpidNumber("");
                                        setIsValidISBN(false);
                                    }}
                                    aria-label="Clear ISBN"
                                >
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/manageaccesscodeclear-icon.svg`}
                                        alt="Clear"
                                    />
                                </button>
                            )}

                        </div>
                    </div>

                    {/* Search Button */}
                    <button
                        className="search-button"
                        onClick={handleSearch}
                        disabled={isLoadingISBN}
                    >
                        <span className="search-text">
                            {isLoadingISBN ? "Loading..." : "Search"}
                        </span>
                    </button>

                    {/* PPID Title */}
                    <div className="field-box">
                        <label className="field-label">PPID Title</label>
                        <div className="field-container ppid-field" aria-disabled="true">
                            <span
                                ref={ppidTitleRef}
                                className={`field-value placeholder-ppid-title`}
                                onMouseEnter={() => {
                                    if (ppidTitleRef.current) {
                                        setIsTruncated(
                                            ppidTitleRef.current.scrollWidth > ppidTitleRef.current.clientWidth
                                        );
                                    }
                                }}
                                title={isTruncated ? ppidTitle : ""}
                            >
                                {ppidTitle ? ppidTitle : "PPID title will display here"}
                            </span>
                        </div>
                    </div>

                    {/* PPID Number */}
                    <div className="field-box">
                        <label className="field-label">PPID Number</label>
                        <div className="field-container ppid-field" aria-disabled="true">
                            <span className={`field-value placeholder-ppid-title`}>{ppidNumber ? ppidNumber : "PPID number will display here"}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageAccessCodeStepOne;
